
import { supabase } from '@/integrations/supabase/client';

export interface DeliveryMetrics {
  totalDeliveries: number;
  onTimeDeliveries: number;
  onTimeRate: number;
}

export const calculateOnTimeRate = async (driverId: string, date?: string): Promise<DeliveryMetrics> => {
  const targetDate = date || new Date().toISOString().split('T')[0];
  
  try {
    // Get all delivered orders for the driver on the target date
    const { data: orders, error } = await supabase
      .from('orders')
      .select('*')
      .eq('driver_id', driverId)
      .eq('status', 'delivered')
      .eq('delivery_type', 'delivery')
      .gte('created_at', `${targetDate}T00:00:00.000Z`)
      .lt('created_at', `${targetDate}T23:59:59.999Z`)
      .not('completed_at', 'is', null);

    if (error) {
      console.error('Error fetching delivery metrics:', error);
      return { totalDeliveries: 0, onTimeDeliveries: 0, onTimeRate: 0 };
    }

    if (!orders || orders.length === 0) {
      return { totalDeliveries: 0, onTimeDeliveries: 0, onTimeRate: 0 };
    }

    let onTimeDeliveries = 0;
    const totalDeliveries = orders.length;

    // For each order, calculate if it was delivered on time
    // Since we don't have exact ready_for_pickup timestamps yet, 
    // we'll estimate based on order creation + typical prep time (15 minutes)
    for (const order of orders) {
      if (order.completed_at && order.created_at) {
        // Estimate when the order was ready (created + 15 minutes prep time)
        const estimatedReadyTime = new Date(new Date(order.created_at).getTime() + 15 * 60 * 1000);
        const deliveredTime = new Date(order.completed_at);
        const timeDiffMinutes = (deliveredTime.getTime() - estimatedReadyTime.getTime()) / (1000 * 60);
        
        // Consider on-time if delivered within 30 minutes of estimated ready time
        if (timeDiffMinutes <= 30) {
          onTimeDeliveries++;
        }
      }
    }

    const onTimeRate = totalDeliveries > 0 ? Math.round((onTimeDeliveries / totalDeliveries) * 100) : 0;
    
    return {
      totalDeliveries,
      onTimeDeliveries,
      onTimeRate
    };
  } catch (error) {
    console.error('Error calculating on-time rate:', error);
    return { totalDeliveries: 0, onTimeDeliveries: 0, onTimeRate: 0 };
  }
};
